import type { U3141LotDetailOperationTypeFromInitLU } from "../../U3141Service";

// Enum for logical unit types avec labels multilingues
export const U3141LotOperationTypeEnum = [
  {
    value: "origin",
    label: {
      fr: "Origine",
      en: "Original",
    },
  },
  {
    value: "subdivision",
    label: {
      fr: "Subdivision",
      en: "Subdivision",
    },
  },
  {
    value: "renovation",
    label: {
      fr: "Rénovation",
      en: "Renovation",
    },
  },
  {
    value: "replacement",
    label: {
      fr: "Remplacement",
      en: "Replacement",
    },
  },
  {
    value: "cancellation",
    label: {
      fr: "Annulation",
      en: "Cancellation",
    },
  },
];

type LangKey = keyof (typeof U3141LotOperationTypeEnum)[0]["label"];

function labelFromInitLuTypes(
  value: string,
  language: string,
  initTypes: U3141LotDetailOperationTypeFromInitLU[],
): string | null {
  const entry = initTypes.find((t) => t.operationTypeCode === value);
  if (!entry?.translations?.length) return null;
  const want = language.startsWith("en") ? "en" : "fr";
  const tr = entry.translations.find((x) => x.languageCode === want) ?? entry.translations.find((x) => x.languageCode === "fr");
  return tr?.operationTypeLabel ?? null;
}

/** Retourne le libellé traduit du type d'opération (initLU si fourni, sinon enum statique). */
export function getOperationTypeLabel(
  value: string | undefined | null,
  language: string,
  initTypes?: U3141LotDetailOperationTypeFromInitLU[],
): string {
  if (value == null || value === "") return "";
  if (initTypes && initTypes.length > 0) {
    const fromInit = labelFromInitLuTypes(value, language, initTypes);
    if (fromInit != null && fromInit !== "") return fromInit;
  }
  const found = U3141LotOperationTypeEnum.find((type) => type.value === value);
  if (!found) return value;
  const key = (language.startsWith("en") ? "en" : "fr") as LangKey;
  return (found.label as Record<LangKey, string>)[key] ?? (found.label as Record<string, string>).fr ?? value;
}

function isInitOperationTypeActive(t: U3141LotDetailOperationTypeFromInitLU): boolean {
  return t.active !== false;
}

/** Options radio à partir de initLU (types actifs + type courant si inactif mais déjà sélectionné), tri par libellé ; enum statique si init vide. */
export function buildLotDetailOperationTypeRadioOptions(
  initTypes: U3141LotDetailOperationTypeFromInitLU[] | undefined,
  language: string,
  selectedOperationTypeCode?: string | null,
): { value: string; label: string }[] {
  const lang = language || "fr";
  if (initTypes && initTypes.length > 0) {
    const byCode = new Map<string, { value: string; label: string }>();
    for (const t of initTypes) {
      if (!isInitOperationTypeActive(t)) continue;
      const label = labelFromInitLuTypes(t.operationTypeCode, lang, initTypes) ?? t.operationTypeCode;
      byCode.set(t.operationTypeCode, { value: t.operationTypeCode, label });
    }
    const sel = selectedOperationTypeCode?.trim();
    if (sel && !byCode.has(sel)) {
      const inactiveRow = initTypes.find((t) => t.operationTypeCode === sel);
      if (inactiveRow) {
        const label = labelFromInitLuTypes(sel, lang, initTypes) ?? sel;
        byCode.set(sel, { value: sel, label });
      }
    }
    return [...byCode.values()].sort((a, b) => a.label.localeCompare(b.label, lang, { sensitivity: "base" }));
  }
  return U3141LotOperationTypeEnum.map((type) => ({
    value: type.value,
    label: typeof type.label === "object" ? (type.label as Record<string, string>)[lang.startsWith("en") ? "en" : "fr"] : String(type.label),
  })).sort((a, b) => a.label.localeCompare(b.label, lang, { sensitivity: "base" }));
}

export function isLotDetailSupplementaryEnabledForOperationType(
  operationTypeCode: string | null | undefined,
  initTypes: U3141LotDetailOperationTypeFromInitLU[] | undefined,
): boolean {
  if (!operationTypeCode?.trim() || !initTypes?.length) return false;
  const row = initTypes.find((t) => t.operationTypeCode === operationTypeCode);
  return row?.lotDetailSupplementaryInfoEnabled === true;
}

/** Au moins un type actif autorise le complément (saisie possible après choix du type). */
export function anyLotDetailOperationTypeHasSupplementary(initTypes: U3141LotDetailOperationTypeFromInitLU[] | undefined): boolean {
  return Boolean(initTypes?.some((t) => isInitOperationTypeActive(t) && t.lotDetailSupplementaryInfoEnabled === true));
}

/**
 * Libellés des types d'opération pour lesquels la saisie d'informations supplémentaires est activée (initLU),
 * triés par libellé courant, séparés par une virgule (ex. « Annulation » seul ou « Annulation, … »).
 */
export function getOperationTypeLabelsWithSupplementaryInfoEnabled(
  initTypes: U3141LotDetailOperationTypeFromInitLU[] | undefined,
  language: string,
): string {
  if (!initTypes?.length) return "";
  const lang = language || "fr";
  const codes = initTypes.filter((t) => t.lotDetailSupplementaryInfoEnabled === true).map((t) => t.operationTypeCode);
  const labels = codes.map((code) => getOperationTypeLabel(code, lang, initTypes)).filter((s) => String(s).trim() !== "");
  const uniq = [...new Set(labels)];
  uniq.sort((a, b) => a.localeCompare(b, lang, { sensitivity: "base" }));
  return uniq.join(", ");
}
